import type { IconName } from './Icon';
import type { Feature, FeatureMap, ProfileType } from 'shared';

/**
 * Разделы приложения и их порядок — один список на обе навигации.
 *
 * Раньше боковая панель и нижнее меню держали свои копии, и они разъехались:
 * «Аналитика» была на широком экране и пропала в Telegram — в мобильном
 * списке её просто забыли. Пока источник один, такое невозможно.
 */
export type ScreenName =
  | 'feed'
  | 'publish'
  | 'mycargos'
  | 'trucks'
  | 'orders'
  | 'company'
  | 'money'
  | 'analytics'
  | 'dispatch'
  | 'profile'
  | 'admin';

export interface NavItem {
  name: ScreenName;
  icon: IconName;
  label: string;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

/**
 * Всё, что относится к грузу — поиск, публикация, отклики — стоит рядом:
 * человек, который ищет груз, не должен перебирать меню целиком. Дальше
 * идёт исполнение (рейсы и машины), потом аккаунт.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Грузы',
    items: [
      { name: 'feed', icon: 'box', label: 'Найти груз' },
      { name: 'publish', icon: 'plus', label: 'Разместить' },
      { name: 'mycargos', icon: 'list', label: 'Мои заявки' },
    ],
  },
  {
    title: 'Перевозка',
    items: [
      { name: 'orders', icon: 'route', label: 'Рейсы' },
      { name: 'money', icon: 'chart', label: 'Финансы' },
      { name: 'analytics', icon: 'bars', label: 'Аналитика' },
      /* Название то же, что и в заголовке экрана. «Машины» рядом с
         «Компанией», где лежит парк, читалось как парк — и раздел, где
         объявляют свободную машину, просто не находили. */
      { name: 'trucks', icon: 'truck', label: 'Свободные машины' },
    ],
  },
  {
    title: 'Аккаунт',
    items: [
      { name: 'company', icon: 'users', label: 'Компания' },
      { name: 'profile', icon: 'user', label: 'Профиль' },
    ],
  },
];

export const ADMIN_GROUP: NavGroup = {
  title: 'Управление',
  items: [
    { name: 'dispatch', icon: 'route', label: 'Диспетчерская' },
    { name: 'admin', icon: 'shield', label: 'Админка' },
  ],
};

/**
 * Что видит сотрудник поддержки.
 *
 * Диспетчерская без админки: помочь с рейсом можно, ничего не сломав.
 * Отдельная группа, а не урезание админской, потому что «покажем всё,
 * а лишнее спрячем в интерфейсе» рано или поздно перестаёт прятать.
 */
export const SUPPORT_GROUP: NavGroup = {
  title: 'Поддержка',
  items: [{ name: 'dispatch', icon: 'route', label: 'Диспетчерская' }],
};

/** Подпись раздела в шапке Telegram: там больше нет других ориентиров. */
export const SCREEN_TITLE: Record<ScreenName, string> = {
  feed: 'Грузы',
  publish: 'Новая заявка',
  mycargos: 'Мои заявки',
  trucks: 'Свободные машины',
  orders: 'Рейсы',
  company: 'Компания',
  money: 'Финансы',
  analytics: 'Аналитика',
  dispatch: 'Диспетчерская',
  profile: 'Профиль',
  admin: 'Администрирование',
};

/**
 * Какой выключатель гасит раздел. Разделов без выключателя больше, чем
 * с ним, поэтому карта частичная: чего здесь нет — видно всегда.
 */
const FEATURE_OF: Partial<Record<ScreenName, Feature>> = {
  money: 'moneyDashboard',
  analytics: 'analytics',
  company: 'companies',
  trucks: 'truckBoard',
};

/** Виден ли раздел при текущих настройках площадки. */
export function screenEnabled(name: ScreenName, features: FeatureMap): boolean {
  const f = FEATURE_OF[name];
  return f ? features[f] : true;
}

/**
 * Кому раздел нужен по роду занятий.
 *
 * Доска машин — инструмент того, кто возит: перевозчик объявляет свободное
 * место, экспедитор ищет, чем закрыть плечо. Заказчику она не нужна вовсе,
 * и держать у него вкладку, где он видит только пустой список, — обещание,
 * которого приложение не выполняет.
 */
const PROFILE_OF: Partial<Record<ScreenName, ProfileType[]>> = {
  trucks: ['CARRIER', 'FORWARDER'],
};

function fitsProfile(
  name: ScreenName,
  profile: ProfileType | null,
  isStaff: boolean,
): boolean {
  const allowed = PROFILE_OF[name];
  if (!allowed) return true;
  // Поддержка и администратор смотрят площадку целиком: им раздел нужен
  // не для работы, а чтобы ответить на вопрос о ней.
  if (isStaff) return true;
  return profile !== null && allowed.includes(profile);
}

/**
 * Разделы, доступные человеку: с учётом выключенных функций и должности.
 * Одна функция на обе навигации — иначе списки снова разойдутся.
 */
export function navFor(opts: {
  features: FeatureMap;
  isAdmin: boolean;
  isStaff: boolean;
  profileType: ProfileType | null;
}): NavGroup[] {
  const staffGroup = opts.isAdmin ? ADMIN_GROUP : opts.isStaff ? SUPPORT_GROUP : null;

  return (staffGroup ? [...NAV_GROUPS, staffGroup] : NAV_GROUPS)
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) =>
          screenEnabled(i.name, opts.features) &&
          fitsProfile(i.name, opts.profileType, opts.isStaff || opts.isAdmin),
      ),
    }))
    .filter((g) => g.items.length > 0);
}
